import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { LogOut, Camera, User, BarChart3 } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ImageSolverModal } from '@/components/ImageSolverModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DIFF_META = {
  'سهل': { cls: 'easy', hex: '#2e7d4f' },
  'متوسط': { cls: 'med', hex: '#b9790f' },
  'صعب': { cls: 'hard', hex: '#d1455c' },
};

const OPTION_LABELS = ['أ', 'ب', 'ج', 'د'];

const IconTrophy = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 21h8M12 17v4M7 4h10v5a5 5 0 0 1-10 0zM17 5h3v2a3 3 0 0 1-3 3M7 5H4v2a3 3 0 0 0 3 3" /></svg>
);
const IconCheck = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M8.5 12.5l2.5 2.5 4.5-5" /></svg>
);
const IconX = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M9 9l6 6M15 9l-6 6" /></svg>
);
const IconBolt = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M13 2L4 14h7l-1 8 9-12h-7z" /></svg>
);
const IconMedal = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 8l4 4 5-7 5 7 4-4-2 11H5z" /></svg>
);
const IconTimer = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="13" r="8" /><path d="M12 9v4l2 2M9 2h6" /></svg>
);

function QuestionDialog({ question, open, onOpenChange, onSubmit }) {
  const [selected, setSelected] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (open) {
      setSelected(null);
      setSubmitted(false);
      setResult(null);
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed(p => p + 0.1), 100);
    }
    return () => clearInterval(timerRef.current);
  }, [open]);

  const handleSubmit = async () => {
    if (selected === null) return;
    clearInterval(timerRef.current);
    setSubmitted(true);
    const res = await onSubmit(question.id, selected, Math.round(elapsed * 10) / 10);
    setResult(res);
  };

  if (!question) return null;
  const meta = DIFF_META[question.difficulty] || DIFF_META['متوسط'];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`olq ${meta.cls} max-w-lg p-0`} dir="rtl" data-testid="question-dialog">
        <DialogDescription className="sr-only">سؤال أولمبياد رشيد</DialogDescription>
        <div className="hd">
          <DialogHeader className="space-y-0">
            <DialogTitle asChild>
              <div className="row">
                <span className="qn"><IconTrophy />سؤال {question.number}</span>
                <span className="pill" data-testid="question-difficulty">{question.difficulty}</span>
              </div>
            </DialogTitle>
          </DialogHeader>
          <span className="tm"><IconTimer /><span data-testid="question-timer">{elapsed.toFixed(1)} ثانية</span></span>
        </div>

        <div className="body">
          <p className="qt" data-testid="question-text">{question.question}</p>
          {question.lesson_title && <p className="lsn">من درس: {question.lesson_title}</p>}

          <div className="opts">
            {(question.options || []).map((opt, i) => {
              let cls = 'opt';
              if (submitted && result) {
                if (i === result.correct_answer) cls += ' correct';
                else if (i === selected && !result.is_correct) cls += ' wrong';
              } else if (selected === i) {
                cls += ' sel';
              }
              return (
                <button
                  key={i}
                  onClick={() => !submitted && setSelected(i)}
                  disabled={submitted}
                  className={cls}
                  data-testid={`option-${i}`}
                >
                  <span className="lbl">{OPTION_LABELS[i]}</span>
                  <span>{opt}</span>
                  {submitted && result && i === result.correct_answer && (
                    <svg className="mk" viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7" /></svg>
                  )}
                  {submitted && result && i === selected && !result.is_correct && i !== result.correct_answer && (
                    <svg className="mk" viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
                  )}
                </button>
              );
            })}
          </div>

          {!submitted ? (
            <button onClick={handleSubmit} disabled={selected === null} className="go" data-testid="submit-answer-btn">
              <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="1.5" fill="currentColor" /></svg>
              تأكيد الإجابة
            </button>
          ) : result && (
            <div className={`res ${result.is_correct ? 'good' : 'bad'}`} data-testid="answer-result">
              {result.is_correct ? (
                <div className="ln"><IconCheck />إجابة صحيحة! <span style={{ fontWeight: 600, fontSize: 13 }}>({elapsed.toFixed(1)} ثانية)</span></div>
              ) : (
                <div className="ln"><IconX />إجابة خاطئة</div>
              )}
              <small>المحاولة رقم {result.attempt_count}</small>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function OlympicsPage() {
  const { subjectId } = useParams();
  const navigate = useNavigate();
  const { user, logout, getAuthHeaders } = useAuth();
  const [data, setData] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeQ, setActiveQ] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filter, setFilter] = useState('all');
  const [solverOpen, setSolverOpen] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [qRes, lbRes] = await Promise.all([
        axios.get(`${API_URL}/api/olympics/${subjectId}/questions`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/olympics/${subjectId}/leaderboard`, { headers: getAuthHeaders() }),
      ]);
      setData(qRes.data);
      setLeaderboard(lbRes.data);
    } catch (err) {
      toast.error('فشل في تحميل الأولمبياد');
    } finally {
      setLoading(false);
    }
  }, [subjectId, getAuthHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async (questionId, answer, timeTaken) => {
    try {
      const res = await axios.post(`${API_URL}/api/olympics/submit`, {
        question_id: questionId,
        answer,
        time_taken: timeTaken,
        subject_id: subjectId,
      }, { headers: getAuthHeaders() });
      setTimeout(() => fetchData(), 500);
      return res.data;
    } catch {
      toast.error('حدث خطأ');
      return null;
    }
  };

  const openQuestion = (q) => {
    setActiveQ(q);
    setDialogOpen(true);
  };

  const filtered = data?.questions?.filter(q => {
    if (filter === 'all') return true;
    if (filter === 'unsolved') return !q.solved;
    if (filter === 'solved') return q.solved;
    return q.difficulty === filter;
  }) || [];

  const stats = data?.student_stats;
  const cu = leaderboard?.current_user;
  const questions = data?.questions || [];
  const pct = stats && stats.total > 0 ? Math.round((stats.solved / stats.total) * 100) : 0;

  const chips = [
    { id: 'all', label: `الكل (${questions.length})` },
    { id: 'unsolved', label: `لم أحل (${questions.filter(q => !q.solved).length})` },
    { id: 'solved', label: `محلولة (${questions.filter(q => q.solved).length})` },
    { id: 'سهل', label: `سهل (${questions.filter(q => q.difficulty === 'سهل').length})` },
    { id: 'متوسط', label: `متوسط (${questions.filter(q => q.difficulty === 'متوسط').length})` },
    { id: 'صعب', label: `صعب (${questions.filter(q => q.difficulty === 'صعب').length})` },
  ];

  return (
    <div className="olpage" dir="rtl" data-testid="olympics-page">
      {/* Header */}
      <header className="nav" data-testid="main-header">
        <Link to="/grades" className="brand">RasheedGPT</Link>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="who" data-testid="user-menu-trigger">
              <i aria-hidden="true">{user?.name?.charAt(0)}</i>
              <span className="hidden sm:inline">{user?.name}</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-48 bg-white border-black/10 text-[#1f1a33]">
            <div className="px-3 py-2 border-b border-black/5">
              <p className="font-semibold text-[#1f1a33]">{user?.name}</p>
              <p className="text-xs text-[#625c78]">{user?.role === 'admin' ? 'مدير' : user?.role === 'student' ? 'طالب' : 'ولي أمر'}</p>
            </div>
            {user?.role === 'admin' && (
              <DropdownMenuItem asChild className="text-[#4a3aa8] hover:text-[#4a3aa8] hover:bg-black/5 cursor-pointer">
                <Link to="/admin"><BarChart3 className="w-4 h-4 me-2" />لوحة التحكم</Link>
              </DropdownMenuItem>
            )}
            {user?.role !== 'admin' && (
              <DropdownMenuItem asChild className="text-[#1f1a33]/80 hover:text-[#1f1a33] hover:bg-black/5 cursor-pointer">
                <Link to="/profile"><User className="w-4 h-4 me-2" />الملف الشخصي</Link>
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={() => setSolverOpen(true)} className="text-[#1f1a33]/80 hover:text-[#1f1a33] hover:bg-black/5 cursor-pointer">
              <Camera className="w-4 h-4 me-2" />حل سؤال
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => { logout(); navigate('/login'); }} className="text-red-500 hover:text-red-600 hover:bg-black/5 cursor-pointer">
              <LogOut className="w-4 h-4 me-2" />تسجيل الخروج
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      <div className="rings" aria-hidden="true"><i></i><i></i><i></i></div>

      <main>
        <button className="back" onClick={() => navigate(-1)} data-testid="back-button">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
          العودة
        </button>

        {loading ? (
          <>
            <div className="sk" style={{ height: 150, marginTop: 20 }} />
            <div className="gd" style={{ marginTop: 24 }}>
              {Array.from({ length: 30 }).map((_, i) => <div key={i} className="sk" style={{ aspectRatio: '1' }} />)}
            </div>
          </>
        ) : !questions.length ? (
          <div className="empty" data-testid="olympics-empty">
            <IconTrophy />
            <h3>لم يتم تفعيل الأولمبياد بعد</h3>
            <p>سيتم إضافة أسئلة الأولمبياد قريبًا</p>
          </div>
        ) : (
          <>
            {/* Hero */}
            <section className="cd hero" aria-label="ملخص الأولمبياد" data-testid="olympics-hero">
              <div>
                <div className="ttl">
                  <div className="tr"><IconTrophy /></div>
                  <div>
                    <h1>أولمبياد <span>{data.subject?.name_ar}</span></h1>
                    <p className="sub">{data.subject?.grade_name}</p>
                  </div>
                </div>
                {cu && (
                  <div className="mt">
                    <span className="m" style={{ '--c': 'var(--med)' }}><IconMedal />ترتيبك: {cu.rank} من {leaderboard?.total_students}</span>
                    <span className="m" style={{ '--c': 'var(--easy)' }}><IconCheck />{cu.correct} صحيحة</span>
                    <span className="m" style={{ '--c': 'var(--hard)' }}><IconX />{cu.wrong} خاطئة</span>
                    <span className="m" style={{ '--c': 'var(--pu)' }}><IconBolt />{cu.total_attempts} محاولة</span>
                  </div>
                )}
              </div>
              {stats && (
                <div className="sts">
                  <div className="st" style={{ '--c': 'var(--pu)' }}><b>{stats.total}</b><span>إجمالي</span></div>
                  <div className="st" style={{ '--c': 'var(--med)' }}><b>{stats.remaining}</b><span>متبقية</span></div>
                  <div className="st" style={{ '--c': 'var(--easy)' }}><b>{stats.solved}</b><span>محلولة</span></div>
                </div>
              )}
              {stats && stats.total > 0 && (
                <div className="pg">
                  <div className="bar" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}>
                    <i style={{ width: `${Math.max(pct, 2)}%` }} />
                  </div>
                  <small>{pct}% مكتمل</small>
                </div>
              )}
            </section>

            {/* Leaderboard */}
            <div className="lbw">
              <button className="lbb" aria-expanded={showLeaderboard} onClick={() => setShowLeaderboard(p => !p)} data-testid="toggle-leaderboard">
                <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="14" r="5" /><path d="M8.5 3L12 9l3.5-6M10 14l1.5 1.5L14.5 12.5" /></svg>
                {showLeaderboard ? 'إخفاء لوحة الترتيب' : 'عرض لوحة الترتيب'}
                <svg className="cv" viewBox="0 0 24 24" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
              </button>
              <div className={`lbp ${showLeaderboard ? 'on' : ''}`}>
                <div>
                  <div className="cd lbl" data-testid="leaderboard-card">
                    {leaderboard?.leaderboard?.length ? leaderboard.leaderboard.map((entry) => (
                      <div key={entry.rank} className={`lr ${entry.is_current ? 'me' : ''}`} data-testid={`leaderboard-entry-${entry.rank}`}>
                        <span className="rk">{entry.rank}</span>
                        <span className="nm">{entry.name}</span>
                        <span className="sc">{entry.correct} صحيحة · {entry.total_time}ث</span>
                      </div>
                    )) : (
                      <div className="lr"><span className="nm" style={{ color: 'var(--mut)', fontWeight: 600 }}>لا يوجد متسابقون بعد</span></div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Filters */}
            <div className="chips" role="group" aria-label="تصفية الأسئلة" data-testid="question-filters">
              {chips.map(c => (
                <button key={c.id} className="chip" aria-pressed={filter === c.id} onClick={() => setFilter(c.id)} data-testid={`filter-${c.id}`}>
                  {c.label}
                </button>
              ))}
            </div>

            {/* Questions grid */}
            <div className="gd" data-testid="questions-grid">
              {filtered.map((q, i) => {
                const meta = DIFF_META[q.difficulty] || DIFF_META['متوسط'];
                const diffCls = meta.cls === 'med' ? 'm2' : meta.cls === 'hard' ? 'h' : '';
                const state = q.solved ? 'ok' : (q.attempts > 0 ? 'no' : '');
                return (
                  <button
                    key={q.id}
                    onClick={() => openQuestion(q)}
                    className={`q ${diffCls} ${state}`}
                    style={{ '--i': i }}
                    aria-label={`السؤال ${q.number}${q.solved ? '، تمت الإجابة' : ''}`}
                    data-testid={`question-cell-${q.number}`}
                  >
                    {q.solved ? (
                      <span className="bd"><IconCheck /></span>
                    ) : q.attempts > 0 ? (
                      <span className="bd">{q.attempts}</span>
                    ) : null}
                    <b>{q.number}</b>
                    <i />
                  </button>
                );
              })}
            </div>

            {/* Legend */}
            <div className="lg" aria-label="دليل الألوان">
              <span style={{ '--c': 'var(--easy)' }}><i className="sq" />محلولة</span>
              <span style={{ '--c': 'var(--hard)' }}><i className="sq" />تمت المحاولة</span>
              <span style={{ '--c': 'var(--easy)' }}><i className="dt" />سهل</span>
              <span style={{ '--c': 'var(--med)' }}><i className="dt" />متوسط</span>
              <span style={{ '--c': 'var(--hard)' }}><i className="dt" />صعب</span>
            </div>
          </>
        )}
      </main>

      <QuestionDialog question={activeQ} open={dialogOpen} onOpenChange={setDialogOpen} onSubmit={handleSubmit} />
      <ImageSolverModal open={solverOpen} onOpenChange={setSolverOpen} />
    </div>
  );
}
